// ============================================================
//  VEHICLE VERIFICATIONS PAGE — ADMIN DASHBOARD
//  Path: src/pages/VehicleVerificationsPage.tsx
//
//  Global review queue for pending ExpertVehicle rows (car/van/
//  moped/motorcycle require document review before approval —
//  bicycle/ebike are auto-approved and never appear here at
//  all, see expertVehicle.service.js). Same reject-reason-modal
//  pattern as AdminsPage.tsx's password reset modal.
//
//  FIX (review pass):
//  - A failed load said "No pending vehicle verifications" (reads as
//    "all clear"); it now shows the error with Try again.
//  - Approve failures were console-only; they're shown now. Approve also
//    asks first, reminding the admin to check the documents — approving
//    unlocks jobs that need that vehicle.
//  - customer_service can list this queue but PATCH /vehicles/:id/review
//    is super_admin / admin only: View Only badge instead of buttons that
//    always fail.
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listPendingVehicleVerifications, reviewVehicle } from '../services/admin.service'
import { AdminExpertVehicle } from '../types'
import { useAuth } from '../context/AuthContext'

// Every member of the VehicleType enum in schema.prisma. 'truck' and
// 'walking' were missing, so those rows fell through to the raw enum
// value — and 'truck' genuinely reaches this queue, since it is not in
// expertVehicle.service.js's NO_DOCUMENT_VEHICLE_TYPES and therefore
// requires document review like any other motorized vehicle.
const VEHICLE_LABELS: Record<string, string> = {
  car: 'Car', van: 'Van', truck: 'Truck',
  bicycle: 'Bicycle', ebike: 'E-bike', walking: 'On foot',
  moped: 'Moped / Motor Scooter', motorcycle: 'Motorcycle / Gas Scooter',
}

const VehicleVerificationsPage: React.FC = () => {
  const { user } = useAuth()
  const canModify = !!(user?.roles.includes('super_admin') || user?.roles.includes('admin'))

  const [vehicles, setVehicles] = useState<AdminExpertVehicle[]>([])
  const [loading, setLoading]   = useState(true)
  const [actingId, setActingId] = useState<string | null>(null)
  const [loadError, setLoadError]     = useState('')
  const [actionError, setActionError] = useState('')

  const [rejectModal, setRejectModal]     = useState<AdminExpertVehicle | null>(null)
  const [rejectReason, setRejectReason]   = useState('')
  const [rejectError, setRejectError]     = useState('')
  const [rejecting, setRejecting]         = useState(false)

  const fetchVehicles = async () => {
    setLoading(true)
    setLoadError('')
    try {
      const res = await listPendingVehicleVerifications()
      setVehicles(res.data.data)
    } catch (err: any) {
      console.error('Failed to fetch pending vehicle verifications:', err)
      setVehicles([])
      setLoadError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load pending vehicle verifications.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchVehicles() }, [])

  const handleApprove = async (vehicle: AdminExpertVehicle) => {
    if (!canModify) return
    const name = `${vehicle.expert.user.firstName} ${vehicle.expert.user.lastName}`
    const label = VEHICLE_LABELS[vehicle.vehicleType] || vehicle.vehicleType
    if (!window.confirm(
      `Approve ${name}'s ${label}?\n\nThey will be able to take jobs that need this vehicle. Make sure you've checked their vehicle documents (View Documents) first.`
    )) return

    setActionError('')
    setActingId(vehicle.id)
    try {
      await reviewVehicle(vehicle.id, { approved: true })
      fetchVehicles()
    } catch (err: any) {
      console.error('Failed to approve vehicle:', err)
      setActionError(err?.response?.data?.message || `Failed to approve ${name}'s ${label} — try again.`)
    } finally {
      setActingId(null)
    }
  }

  const openRejectModal = (vehicle: AdminExpertVehicle) => {
    setRejectModal(vehicle)
    setRejectReason('')
    setRejectError('')
  }

  const handleReject = async () => {
    if (!rejectModal || !canModify) return
    if (!rejectReason.trim()) {
      setRejectError('A reason is required so the expert knows what to fix')
      return
    }
    setRejecting(true)
    try {
      await reviewVehicle(rejectModal.id, { approved: false, rejectionReason: rejectReason.trim() })
      setRejectModal(null)
      fetchVehicles()
    } catch (err: any) {
      setRejectError(err.response?.data?.message || 'Failed to reject vehicle verification')
    } finally {
      setRejecting(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Vehicle Verifications</h1>
          <p className="text-gray-400 text-sm mt-1">
            Experts awaiting document review for a vehicle (car, van, truck, moped, or motorcycle).
            Bicycle and e-bike are approved automatically and never appear here.
          </p>
        </div>
        {!canModify && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400 shrink-0">View Only</span>
        )}
      </div>

      {loadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{loadError}</p>
          <button onClick={() => fetchVehicles()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      )}
      {actionError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {actionError}
          <button onClick={() => setActionError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Vehicle Type</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Requested</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : loadError ? (
              <tr><td colSpan={4} className="text-center py-8 text-gray-500">—</td></tr>
            ) : vehicles.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-8 text-gray-400">No pending vehicle verifications</td></tr>
            ) : (
              vehicles.map((v) => (
                <tr key={v.id} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4">
                    <Link to={`/experts/${v.expert.id}`} className="text-sm font-medium text-brand-400 hover:text-brand-300">
                      {v.expert.user.firstName} {v.expert.user.lastName}
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{VEHICLE_LABELS[v.vehicleType] || v.vehicleType}</td>
                  <td className="px-5 py-4 text-sm text-gray-400">{new Date(v.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      {canModify && (
                        <>
                          <button
                            onClick={() => handleApprove(v)}
                            disabled={actingId === v.id}
                            className="px-3 py-1.5 text-xs bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50"
                          >
                            {actingId === v.id ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => openRejectModal(v)}
                            disabled={actingId === v.id}
                            className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <Link
                        to={`/experts/${v.expert.id}`}
                        className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors"
                      >
                        View Documents
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rejectModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Reject Vehicle Verification</h3>
            <p className="text-sm text-gray-400 mb-4">
              {rejectModal.expert.user.firstName} {rejectModal.expert.user.lastName} — {VEHICLE_LABELS[rejectModal.vehicleType] || rejectModal.vehicleType}
            </p>

            {rejectError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">{rejectError}</p>
              </div>
            )}

            <textarea
              placeholder="Reason for rejection (shown to the expert)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4"
            />

            <div className="flex gap-3 justify-end">
              <button onClick={() => setRejectModal(null)}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={handleReject} disabled={rejecting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors">
                {rejecting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VehicleVerificationsPage